import { useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Package, ArrowLeft, ExternalLink, Loader2, User, LogOut, ChevronRight,
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  getInfo, getMe, getMeusPedidos, logout, fmtPreco, getLojaToken,
} from './lojaApi'
import LojaLayout from './LojaLayout'

const STATUS_LABEL: Record<string, string> = {
  aberto: 'Em aberto · em análise',
  aprovado: 'Aprovado',
  separado: 'Separado',
  embalado: 'Embalado',
  expedido: 'Saiu para entrega',
  entregue: 'Entregue',
  cancelado: 'Cancelado',
  faturado: 'Faturado',
}

export default function LojaMeusPedidos() {
  const { slug = '' } = useParams()
  const navigate = useNavigate()
  const token = getLojaToken()

  useEffect(() => {
    if (!token) {
      navigate(`/loja/${slug}/login?redirect=/loja/${slug}/minha-conta`, { replace: true })
    }
  }, [token, slug, navigate])

  const { data: info } = useQuery({
    queryKey: ['loja-info', slug],
    queryFn: () => getInfo(slug),
    enabled: !!slug,
  })

  const { data: me } = useQuery({
    queryKey: ['loja-me'],
    queryFn: () => getMe(),
    enabled: !!token,
    retry: false,
  })

  const { data: pedidos, isLoading } = useQuery({
    queryKey: ['loja-meus-pedidos'],
    queryFn: () => getMeusPedidos(),
    enabled: !!token,
    retry: false,
  })

  async function sair() {
    await logout()
    toast.success('Você saiu da conta.')
    navigate(`/loja/${slug}`, { replace: true })
  }

  if (!token) {
    return (
      <LojaLayout info={info || null} slug={slug}>
        <div className="max-w-md mx-auto p-8 text-center">
          <Loader2 className="mx-auto animate-spin text-ink-400" size={24} />
        </div>
      </LojaLayout>
    )
  }

  return (
    <LojaLayout info={info || null} slug={slug}>
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-4">
        <Link to={`/loja/${slug}`} className="btn-ghost text-sm inline-flex">
          <ArrowLeft size={14} /> Voltar ao catálogo
        </Link>

        {/* Cabeçalho do usuário */}
        <div className="card card-body">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center shrink-0">
                <User size={24} />
              </div>
              <div>
                <div className="font-semibold text-ink-900">{me?.nome || 'Carregando...'}</div>
                <div className="text-xs text-ink-500">{me?.email}</div>
                {me?.telefone && <div className="text-xs text-ink-500 mt-0.5">{me.telefone}</div>}
              </div>
            </div>
            <button type="button" onClick={sair} className="btn-ghost text-sm">
              <LogOut size={14} /> Sair
            </button>
          </div>
        </div>

        {/* Pedidos */}
        <div>
          <h2 className="text-lg font-semibold text-ink-900 mb-3 flex items-center gap-2">
            <Package size={18} /> Meus pedidos
          </h2>

          {isLoading && (
            <div className="card card-body text-center py-8">
              <Loader2 className="mx-auto animate-spin text-ink-400" size={20} />
            </div>
          )}

          {!isLoading && (!pedidos || pedidos.itens.length === 0) && (
            <div className="card card-body text-center py-10">
              <Package size={28} className="mx-auto mb-2 text-ink-300" />
              <div className="font-medium text-ink-700">Nenhum pedido ainda</div>
              <div className="text-xs text-ink-500 mt-1 mb-4">
                Seus pedidos aparecerão aqui após a primeira compra.
              </div>
              <Link to={`/loja/${slug}`} className="btn-primary inline-flex text-sm">
                Ver catálogo
              </Link>
            </div>
          )}

          {!isLoading && pedidos && pedidos.itens.length > 0 && (
            <div className="card divide-y divide-ink-100">
              {pedidos.itens.map((p) => (
                <Link
                  key={p.id}
                  to={p.rastreio_token ? `/loja/${slug}/rastreio/${p.rastreio_token}` : '#'}
                  className="flex items-center gap-3 p-4 hover:bg-ink-50 transition"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-sm font-semibold text-ink-900">
                        #{p.numero}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-ink-100 text-ink-700 uppercase tracking-wider">
                        {STATUS_LABEL[p.status] || p.status}
                      </span>
                    </div>
                    <div className="text-xs text-ink-500">
                      {p.criado_em && new Date(p.criado_em).toLocaleString('pt-BR')}
                      <span className="mx-1">·</span>
                      <span className="capitalize">{p.forma_pagamento.replace('_', ' ')}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-semibold text-ink-900">R$ {fmtPreco(p.total)}</div>
                    {p.rastreio_token && (
                      <div className="text-[10px] text-brand-600 flex items-center gap-1 justify-end mt-0.5">
                        Acompanhar <ChevronRight size={10} />
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </LojaLayout>
  )
}
